import { motion, AnimatePresence } from "framer-motion";
import { Check, Clock, Lock, Sparkles } from "lucide-react";
import {
  RHYTHM_PATH_MISSIONS,
  isRhythmPathMissionUnlocked,
  type RhythmPathMission,
} from "../../../lib/rhythm/rhythmPathPlan";
import type { RhythmPathProgress } from "../../../lib/rhythm/rhythmPathProgress";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import { BEGINNER_PATH_EXERCISE_GRID_CLASS } from "../../../lib/beginnerPath/beginnerPathLayout";

export interface RhythmPathLibraryProps {
  progress: RhythmPathProgress;
  pathMode?: boolean;
  preview?: boolean;
  onSelectMission: (missionId: string) => void;
}

export function RhythmPathLibrary({
  progress,
  pathMode = false,
  preview = false,
  onSelectMission,
}: RhythmPathLibraryProps) {
  const completed = progress.completedMissionIds.length;
  const total = RHYTHM_PATH_MISSIONS.length;

  return (
    <div className={`w-full ${pathMode ? "pb-8" : "py-4"}`}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          {!pathMode && (
            <>
              <p className="text-xs tracking-widest uppercase text-[#99f6e4]/70">
                Camino 2 · Mano derecha
              </p>
              <h2 className="mt-2 font-serif text-2xl font-light text-white/90">
                Mano derecha monódica
              </h2>
            </>
          )}
          <p className={`text-sm ${pathMode ? "" : "mt-2"} text-white/55`}>
            {completed}/{total} misiones · pasos progresivos (p · i · m)
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/45">
          <Sparkles className="h-4 w-4 text-[#99f6e4]" />
          Metrónomo + mic
        </div>
      </div>

      <div className={BEGINNER_PATH_EXERCISE_GRID_CLASS}>
        <AnimatePresence mode="popLayout">
          {RHYTHM_PATH_MISSIONS.map((mission) => {
            const stepsDone = mission.subMissions.filter((s) =>
              progress.completedSubMissionIds.includes(s.id)
            ).length;
            return (
              <MissionCard
                key={mission.id}
                mission={mission}
                completed={progress.completedMissionIds.includes(mission.id)}
                stepsDone={stepsDone}
                unlocked={isRhythmPathMissionUnlocked(
                  mission,
                  progress.completedMissionIds,
                  preview
                )}
                onOpen={() => onSelectMission(mission.id)}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StepCircleCounter({
  done,
  total,
}: {
  done: number;
  total: number;
}) {
  const size = 40;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const progress = total > 0 ? Math.min(1, done / total) : 0;
  const offset = c * (1 - progress);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${done} de ${total} pasos`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#99f6e4"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-300"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums text-[#99f6e4]/90">
        {done}/{total}
      </span>
    </div>
  );
}

function MissionCard({
  mission,
  completed,
  stepsDone,
  unlocked,
  onOpen,
}: {
  mission: RhythmPathMission;
  completed: boolean;
  stepsDone: number;
  unlocked: boolean;
  onOpen: () => void;
}) {
  const stepsTotal = mission.subMissions.length;

  return (
    <motion.button
      type="button"
      layout
      disabled={!unlocked}
      onClick={onOpen}
      className="relative text-left rounded-2xl border p-5 transition-all disabled:opacity-45 disabled:cursor-not-allowed"
      style={{
        borderColor: completed ? PATH.cardBorderMint : PATH.cardBorder,
        background: completed ? "rgba(153, 246, 228, 0.08)" : PATH.cardBg,
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold"
            style={{ background: "rgba(153, 246, 228, 0.15)", color: "#99f6e4" }}
          >
            {mission.order}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#99f6e4]/80 tabular-nums">
            {mission.bpm} BPM
          </span>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {completed ? (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
              <Check className="h-3.5 w-3.5" /> Hecho
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] text-white/40">
              <Clock className="h-3 w-3" /> ~{mission.timeEstimateMin} min
            </span>
          )}
          <StepCircleCounter done={stepsDone} total={stepsTotal} />
        </div>
      </div>

      <h3 className="font-serif text-lg text-white/90">{mission.title}</h3>
      <p className="mt-1 text-xs text-white/50">{mission.subtitle}</p>
      <p className="mt-2 text-[11px] text-white/40 line-clamp-2">{mission.description}</p>

      {!unlocked && (
        <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-white/35">
          <Lock className="h-3 w-3" /> Bloqueada
        </span>
      )}
    </motion.button>
  );
}
